import axios, { type InternalAxiosRequestConfig } from 'axios';
import {
  clearStoredAuthSession,
  IDEMPOTENCY_STORAGE_PREFIX,
  USER_STORAGE_KEY,
  getSessionEndReasonMessage,
  isJwtExpired,
  isSessionEndReason,
  type SessionEndReason,
} from '@/lib/authSession';
import { trackApiFailure, trackApiSuccessEvent } from '@/lib/analytics';
import { syncSubscriptionGateFromApiError } from '@/lib/subscriptionGate';
import { syncFounderAgreementGateFromApiError } from '@/lib/founderAgreementGate';
import { isDemoModeEnabled } from '@/demo/config';
import { createDemoAxiosAdapter } from '@/demo/mockApi';

// Clean up the API URL by removing any trailing characters
const cleanApiUrl = import.meta.env.VITE_API_URL?.replace(/[^a-zA-Z0-9:/._-]/g, '') || '';

const api = axios.create({
  baseURL: cleanApiUrl,
  ...(isDemoModeEnabled() ? { adapter: createDemoAxiosAdapter() } : {}),
});

let isHandlingAuthLogout = false;
// De-duplicates GET /auth/session checks triggered by gateway 403s so pollers
// never fan out into a burst of session lookups or a logout loop.
let pendingSessionVerification: Promise<void> | null = null;
let lastValidSessionVerificationAt = 0;
const SESSION_VERIFICATION_COOLDOWN_MS = 30 * 1000;

type AnalyticsRequestConfig = InternalAxiosRequestConfig & {
  analyticsStartedAt?: number;
  retrySafeRequestStorageKey?: string;
};

const IDEMPOTENT_MUTATION_PATH = /generate-ai|smart-edit|shuffle|lab-reports\/extract|nutri-invoices/;
const IDEMPOTENCY_TTL_MS = 15 * 60 * 1000;

const stableRequestValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableRequestValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== 'requestKey')
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, child]) => [key, stableRequestValue(child)]),
  );
};

const fingerprint = (value: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const attachRetrySafeIdempotencyKey = (config: AnalyticsRequestConfig): void => {
  if (config.method?.toLowerCase() !== 'post' || !IDEMPOTENT_MUTATION_PATH.test(config.url ?? '')) return;
  if (config.headers['X-Idempotency-Key'] || config.headers['x-idempotency-key']) return;
  const payload = JSON.stringify(stableRequestValue(config.data ?? null));
  const storageKey = `cs-idempotency:${fingerprint(`${config.url ?? ''}:${payload}`)}`;
  let requestKey: string | null = null;
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null') as {
      key?: unknown;
      createdAt?: unknown;
    } | null;
    if (
      typeof stored?.key === 'string'
      && typeof stored.createdAt === 'number'
      && Date.now() - stored.createdAt < IDEMPOTENCY_TTL_MS
    ) requestKey = stored.key;
  } catch {
    requestKey = null;
  }
  requestKey ||= crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ key: requestKey, createdAt: Date.now() }));
    config.retrySafeRequestStorageKey = storageKey;
  } catch {
    // The header still protects this request even when storage is unavailable.
  }
  config.headers['X-Idempotency-Key'] = requestKey;
};

const clearRetrySafeRequestKey = (config: AnalyticsRequestConfig | undefined): void => {
  if (!config?.retrySafeRequestStorageKey) return;
  try { sessionStorage.removeItem(config.retrySafeRequestStorageKey); } catch { /* no-op */ }
};

const withAnalyticsConfig = (
  config: InternalAxiosRequestConfig | undefined
): AnalyticsRequestConfig | undefined => config as AnalyticsRequestConfig | undefined;

const getAnalyticsDuration = (config: AnalyticsRequestConfig | undefined): number | undefined => {
  if (!config?.analyticsStartedAt) {
    return undefined;
  }

  return performance.now() - config.analyticsStartedAt;
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const data = error.response?.data;
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    if (typeof record.message === 'string') {
      return record.message;
    }
    if (typeof record.error === 'string') {
      return record.error;
    }
  }

  return error.message;
};

const readStoredToken = (): string | null => {
  try {
    const userJson = localStorage.getItem(USER_STORAGE_KEY);
    if (!userJson) {
      return null;
    }
    const user = JSON.parse(userJson) as { token?: unknown };
    if (typeof user.token !== 'string' || !user.token) {
      return null;
    }
    return user.token;
  } catch {
    return null;
  }
};

const isConsentEndpointRequest = (url?: string): boolean =>
  Boolean(url?.includes('/auth/consent'));

// Session management endpoints carry the bearer token and handle their own
// failures; a rejection here must never trigger another session check.
const isSessionManagementRequest = (url?: string): boolean =>
  Boolean(url?.includes('/auth/logout') || url?.includes('/auth/session'));

const isAuthenticatedAuthEndpointRequest = (url?: string): boolean =>
  Boolean(
    isSessionManagementRequest(url)
    || url?.includes('/auth/admin/')
    || url?.includes('/auth/ai-meal-plan-options')
    || url?.includes('/auth/pre-provisioned-roles')
    || url?.includes('/auth/change-password')
    || url?.includes('/auth/subscription')
    || url?.includes('/auth/founder-agreement')
    || isConsentEndpointRequest(url)
  );

const isAuthEndpointRequest = (url?: string): boolean =>
  Boolean(url?.includes('/auth/') && !isAuthenticatedAuthEndpointRequest(url));

const isPublicUnauthenticatedEndpointRequest = (url?: string): boolean =>
  isAuthEndpointRequest(url)
  || Boolean(url?.includes('/questionnaires/share/'))
  || Boolean(url?.includes('/appointments-public/'));

const readRequestReturnPath = (): string => {
  if (typeof window === 'undefined') {
    return '/dashboard';
  }

  const { pathname, search } = window.location;
  if (pathname.startsWith('/auth/subscription-required')) {
    return '/dashboard';
  }

  return `${pathname}${search}`;
};

const logoutAfterAuthFailureResponse = (reason?: SessionEndReason) => {
  if (isHandlingAuthLogout || typeof window === 'undefined') {
    return;
  }

  if (!readStoredToken()) {
    return;
  }

  isHandlingAuthLogout = true;
  clearStoredAuthSession();

  if (window.location.pathname !== '/login') {
    const returnPath = readRequestReturnPath();
    const params = new URLSearchParams({ from: returnPath });
    if (reason) params.set('reason', reason);
    window.location.replace(`/login?${params.toString()}`);
  } else if (reason) {
    const message = getSessionEndReasonMessage(reason);
    if (message) {
      void import('@/utils/toast-utils').then(({ showWarningToast }) => showWarningToast('Signed out', message));
    }
  }
};

// A gateway 403 with a live token is ambiguous (RBAC, consent, subscription or
// a session the server has ended). Ask the API once and sign out only when it
// says the session is gone.
const verifySessionAfterGatewayRejection = (token: string): void => {
  if (pendingSessionVerification || isHandlingAuthLogout || typeof window === 'undefined') {
    return;
  }
  if (Date.now() - lastValidSessionVerificationAt < SESSION_VERIFICATION_COOLDOWN_MS) {
    return;
  }

  pendingSessionVerification = api
    .get<{ valid?: unknown; reason?: unknown }>('/auth/session', {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 2500,
    })
    .then((response) => {
      const data = response.data;
      if (!data || typeof data !== 'object' || typeof data.valid !== 'boolean') {
        return;
      }
      if (data.valid) {
        lastValidSessionVerificationAt = Date.now();
        return;
      }
      if (readStoredToken() !== token) {
        return;
      }
      const reason: SessionEndReason = isSessionEndReason(data.reason) ? data.reason : 'session_revoked';
      logoutAfterAuthFailureResponse(reason);
    })
    .catch((error: unknown) => {
      // The gateway rejecting the session endpoint itself means the token is dead.
      if (
        axios.isAxiosError(error)
        && (error.response?.status === 401 || error.response?.status === 403)
        && readStoredToken() === token
      ) {
        logoutAfterAuthFailureResponse('token_expired');
      }
      // Network errors and 5xx leave the session alone.
    })
    .finally(() => {
      pendingSessionVerification = null;
    });
};

const redirectMissingBillingSessionToLogin = () => {
  if (isHandlingAuthLogout || typeof window === 'undefined') {
    return;
  }

  isHandlingAuthLogout = true;
  const returnPath = readRequestReturnPath();
  window.location.replace(`/login?from=${encodeURIComponent(returnPath)}`);
};

const shouldLogoutAfterAuthError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  if (isPublicUnauthenticatedEndpointRequest(error.config?.url)) {
    return false;
  }

  if (isSessionManagementRequest(error.config?.url)) {
    return false;
  }

  const token = readStoredToken();
  if (!token) {
    return false;
  }

  if (error.response?.status === 401) {
    const code = error.response.data?.code;
    // A rejected login method does not invalidate an authenticated session.
    if (code === 'AUTH_PROVIDER_MISMATCH' || code === 'AUTH_PROVIDER_REVIEW_REQUIRED') {
      return false;
    }
    return true;
  }

  // API Gateway returns a generic 403 when its Lambda authorizer rejects an
  // expired or superseded token.
  const isGatewayAuthorizerRejection =
    error.response?.status === 403
    && Boolean(error.response?.data)
    && typeof error.response.data === 'object'
    && (error.response.data as Record<string, unknown>).message === 'Forbidden';
  if (!isGatewayAuthorizerRejection) {
    return false;
  }

  // On other routes the same 403 can also mean RBAC, consent or subscription
  // blocks, which must not log the user out. An expired token is unambiguous:
  // no request can succeed again, and pollers would otherwise retry forever.
  if (isJwtExpired(token)) {
    return true;
  }

  // A live token that the gateway still rejects may have been idled out,
  // superseded or revoked server-side: confirm with the API before acting.
  verifySessionAfterGatewayRejection(token);
  return false;
};

// Add a request interceptor to include JWT token in all requests
api.interceptors.request.use(
  (config) => {
    const analyticsConfig = config as AnalyticsRequestConfig;
    analyticsConfig.analyticsStartedAt = performance.now();
    const token = readStoredToken();
    attachRetrySafeIdempotencyKey(analyticsConfig);

    // Don't add Authorization header for authentication endpoints
    const isAuthEndpoint = isAuthEndpointRequest(config.url);

    // Don't add Authorization for shared questionnaire link access.
    // Submit endpoints can be used by authenticated nutritionists (on behalf of client),
    // so keep Authorization when a token exists.
    const isPublicQuestionnaire = config.url?.includes('/questionnaires/share/');
    const isPublicBooking = config.url?.includes('/appointments-public/');

    const hasExplicitAuthorizationHeader =
      Boolean(config.headers?.Authorization)
      || Boolean(config.headers?.authorization);

    if (token && !isAuthEndpoint && !isPublicQuestionnaire && !isPublicBooking && !hasExplicitAuthorizationHeader) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return analyticsConfig;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    const config = withAnalyticsConfig(response.config);
    clearRetrySafeRequestKey(config);
    trackApiSuccessEvent({
      durationMs: getAnalyticsDuration(config),
      method: config?.method,
      status: response.status,
      url: config?.url,
    });
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      const config = withAnalyticsConfig(error.config);
      if (error.response && error.response.status < 500) clearRetrySafeRequestKey(config);
      trackApiFailure({
        code: error.code,
        durationMs: getAnalyticsDuration(config),
        message: getApiErrorMessage(error),
        method: config?.method,
        status: error.response?.status,
        url: config?.url,
      });
    }

    if (shouldLogoutAfterAuthError(error)) {
      const token = readStoredToken();
      logoutAfterAuthFailureResponse(token && isJwtExpired(token) ? 'token_expired' : undefined);
    } else {
      const returnPath = readRequestReturnPath();
      syncFounderAgreementGateFromApiError(
        error,
        returnPath,
      );
      syncSubscriptionGateFromApiError(
        error,
        error.config?.url,
        returnPath,
        Boolean(readStoredToken()),
      );
    }

    return Promise.reject(error);
  }
);

export default api;
